import { alignDecorative, packDecorative, segmentsForRoute } from "./decorative-segments.ts";
import { stripClosingVertex } from "./editor-geom.ts";
import {
  alignLineStyles,
  alignPointStyles,
  packLineStyles,
  packPointStyles,
  parseStyleDefaults,
} from "./feature-style.ts";
import { closestPointOnSegment, haversineM, lineDistanceM } from "./geo.ts";
import { sanitizeRegions } from "./region-fill.ts";
import {
  decorativeFromRoutePaint,
  lineStylesFromRoutePaint,
  type RoutePaintFeature,
} from "./quest-route-paint.ts";
import type { LngLat, PieceGraph, WorkArea } from "./types.ts";
import { parseWorkArea, serializeWorkArea } from "./work-area.ts";

export type { PieceGraph };

export type RouteFailure = "empty" | "disconnected";

export interface RoutePlan {
  ok: true;
  closed: boolean;
  start: number;
  /** Vertex indices. A closed route repeats the start at the end. */
  walk: number[];
  coordinates: LngLat[];
  routeLengthM: number;
  uniqueLengthM: number;
  repeatedLengthM: number;
}

export interface RouteRejected {
  ok: false;
  reason: RouteFailure;
}

export type RouteResult = RoutePlan | RouteRejected;

const MATCH_EXACT_LIMIT = 20;

export function routeBlockMessage(reason: RouteFailure): string {
  if (reason === "disconnected") return "Some lines aren't connected, so this route can't be saved.";
  return "Connect at least one line.";
}

/** Quest route line plus its stored editor canvas (missions.route_canvas). */
export function graphFromQuestRoute(
  coordinates: LngLat[],
  closed: boolean,
  canvas?: WorkArea | null,
  paint?: readonly RoutePaintFeature[] | null,
): PieceGraph {
  const graph = graphFromLine(coordinates, closed);
  if (canvas) {
    const workArea = parseWorkArea(canvas);
    if (workArea) graph.workArea = serializeWorkArea(workArea);
  }
  const lineStyles = lineStylesFromRoutePaint(graph.points, graph.segments, paint);
  if (lineStyles) graph.lineStyles = lineStyles;
  const decorative = decorativeFromRoutePaint(graph.points, graph.segments, paint);
  if (decorative) graph.decorative = decorative;
  return graph;
}

/** Turn a stored LineString into a chain, closing the cycle when the line does. */
export function graphFromLine(coordinates: LngLat[], closedFlag = false): PieceGraph {
  const stripped = stripClosingVertex(coordinates);
  const points = stripped.points.map(clone);
  const segments: Array<[number, number]> = [];
  for (let i = 1; i < points.length; i++) link(segments, i - 1, i);
  if ((closedFlag || stripped.closed) && points.length >= 2) {
    link(segments, 0, points.length - 1);
  }
  return {
    points,
    segments,
    start: points.length > 0 ? 0 : null,
  };
}

export function sanitizeGraph(graph: {
  points: LngLat[];
  segments: ReadonlyArray<readonly [number, number]>;
  start?: number | null;
  workArea?: unknown;
  regions?: unknown;
  pointStyles?: unknown;
  lineStyles?: unknown;
  decorative?: unknown;
  styleDefaults?: unknown;
}): PieceGraph {
  const points = graph.points.map(clone);
  const segments: Array<[number, number]> = [];
  for (const seg of graph.segments) link(segments, seg[0], seg[1], points.length);
  let start: number | null = null;
  if (
    typeof graph.start === "number" &&
    Number.isInteger(graph.start) &&
    graph.start >= 0 &&
    graph.start < points.length
  ) {
    start = graph.start;
  }
  const workArea = parseWorkArea(graph.workArea);
  const regions = sanitizeRegions(graph.regions, points.length);
  const out: PieceGraph = { points, segments, start };
  if (workArea) out.workArea = serializeWorkArea(workArea);
  if (regions.length > 0) out.regions = regions;
  const pointStyles = packPointStyles(alignPointStyles(graph.pointStyles, points.length), points.length);
  const lineStyles = packLineStyles(alignLineStyles(graph.lineStyles, segments.length), segments.length);
  const decorative = packDecorative(alignDecorative(graph.decorative, segments.length), segments.length);
  if (pointStyles) out.pointStyles = pointStyles;
  if (lineStyles) out.lineStyles = lineStyles;
  if (decorative) out.decorative = decorative;
  if (graph.styleDefaults != null) {
    out.styleDefaults = parseStyleDefaults(graph.styleDefaults);
  }
  return out;
}

export interface SegmentSplit {
  points: LngLat[];
  segments: Array<[number, number]>;
  /** Index of the vertex inserted on the segment. */
  index: number;
}

/**
 * Replace one segment with two that meet at the closest point on that segment.
 * `at` is snapped onto the segment, never onto a road. When `connectFrom` is
 * another vertex, that vertex is also linked to the new point.
 * Returns null when the hit is an endpoint, so the caller can select it instead.
 */
export function splitSegment(
  points: readonly LngLat[],
  segments: ReadonlyArray<readonly [number, number]>,
  segmentIndex: number,
  at: LngLat,
  connectFrom: number | null = null,
): SegmentSplit | null {
  const seg = segments[segmentIndex];
  if (!seg) return null;
  const [a, b] = seg;
  const endA = points[a];
  const endB = points[b];
  if (!endA || !endB) return null;
  const hit = closestPointOnSegment(at, endA, endB);
  if (hit.t <= 0 || hit.t >= 1) return null;
  if (haversineM(hit.point, endA) < 1.5 || haversineM(hit.point, endB) < 1.5) return null;
  const nextPoints = points.map(clone);
  const index = nextPoints.length;
  nextPoints.push([hit.point[0], hit.point[1]]);
  const nextSegments: Array<[number, number]> = [];
  for (let i = 0; i < segments.length; i++) {
    if (i === segmentIndex) continue;
    nextSegments.push([segments[i][0], segments[i][1]]);
  }
  link(nextSegments, a, index, nextPoints.length);
  link(nextSegments, b, index, nextPoints.length);
  if (connectFrom != null) link(nextSegments, connectFrom, index, nextPoints.length);
  return { points: nextPoints, segments: nextSegments, index };
}

export function resolveStart(graph: PieceGraph): number {
  const n = graph.points.length;
  if (n === 0) return 0;
  if (graph.start != null && graph.start >= 0 && graph.start < n) return graph.start;
  const routeSegs = segmentsForRoute(graph.segments, alignDecorative(graph.decorative, graph.segments.length));
  const deg = degrees(n, routeSegs);
  for (let i = 0; i < n; i++) {
    if (deg[i] % 2 === 1) return i;
  }
  return 0;
}

/**
 * One continuous run that traverses every drawn segment at least once,
 * starting at the chosen vertex. Uses an Euler path when one exists from
 * that start. Otherwise duplicates the shortest set of drawn segments
 * (undirected Chinese postman on this graph only — roads are not used).
 */
export function computeRoute(graph: PieceGraph): RouteResult {
  const points = graph.points;
  const decorative = alignDecorative(graph.decorative, graph.segments.length);
  const segments = segmentsForRoute(graph.segments, decorative);
  const n = points.length;
  if (n === 0 || segments.length === 0) {
    return { ok: false, reason: n > 1 ? "disconnected" : "empty" };
  }
  if (!isConnected(n, segments)) return { ok: false, reason: "disconnected" };

  const start = resolveStart(graph);
  const adj = adjacency(n, points, segments);
  const odd = oddVertices(n, segments);
  const cache = new Map<number, PathRow>();
  const { pairs: bestPairs } = bestCover(adj, cache, odd, start);

  const mult = new Map<string, number>();
  for (const [a, b] of segments) mult.set(edgeKey(a, b), 1);
  for (const [u, v] of bestPairs) {
    const path = shortestPath(adj, cache, u, v);
    for (let i = 1; i < path.length; i++) {
      const key = edgeKey(path[i - 1], path[i]);
      mult.set(key, (mult.get(key) ?? 0) + 1);
    }
  }

  const walk = eulerWalk(start, n, mult);
  const coordinates = walk.map((index) => clone(points[index]));
  const routeLengthM = lineDistanceM(coordinates);
  const uniqueLengthM = segments.reduce((sum, [a, b]) => sum + haversineM(points[a], points[b]), 0);
  const closed = walk.length > 1 && walk[0] === walk[walk.length - 1];
  return {
    ok: true,
    closed,
    start,
    walk,
    coordinates,
    routeLengthM,
    uniqueLengthM,
    repeatedLengthM: Math.max(0, routeLengthM - uniqueLengthM),
  };
}

function link(
  segments: Array<[number, number]>,
  a: number,
  b: number,
  count = Number.POSITIVE_INFINITY,
): void {
  if (!Number.isInteger(a) || !Number.isInteger(b)) return;
  if (a < 0 || b < 0 || a >= count || b >= count || a === b) return;
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  if (segments.some(([x, y]) => x === lo && y === hi)) return;
  segments.push([lo, hi]);
}

function clone(p: LngLat): LngLat {
  return [p[0], p[1]];
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function degrees(n: number, segments: ReadonlyArray<readonly [number, number]>): number[] {
  const deg = Array<number>(n).fill(0);
  for (const [a, b] of segments) {
    if (a >= 0 && a < n) deg[a] += 1;
    if (b >= 0 && b < n) deg[b] += 1;
  }
  return deg;
}

function oddVertices(n: number, segments: ReadonlyArray<readonly [number, number]>): number[] {
  const deg = degrees(n, segments);
  const odd: number[] = [];
  for (let i = 0; i < n; i++) {
    if (deg[i] % 2 === 1) odd.push(i);
  }
  return odd;
}

function isConnected(n: number, segments: ReadonlyArray<readonly [number, number]>): boolean {
  const deg = degrees(n, segments);
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => {
    let v = i;
    while (parent[v] !== v) {
      parent[v] = parent[parent[v]];
      v = parent[v];
    }
    return v;
  };
  for (const [a, b] of segments) {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  }
  let root = -1;
  for (let i = 0; i < n; i++) {
    if (deg[i] === 0) continue;
    const r = find(i);
    if (root < 0) root = r;
    else if (r !== root) return false;
  }
  return root >= 0;
}

type PathRow = { dist: Float64Array; prev: Int32Array };

type Adj = { n: number; head: Int32Array; to: Int32Array; w: Float64Array };

/** Above this, scanning every open end is still cheap. Larger odd sets use one covering. */
const GREEDY_ALL_ENDS = 340;

function adjacency(
  n: number,
  points: LngLat[],
  segments: ReadonlyArray<readonly [number, number]>,
): Adj {
  const deg = new Int32Array(n);
  for (const [a, b] of segments) {
    deg[a] += 1;
    deg[b] += 1;
  }
  const head = new Int32Array(n + 1);
  for (let i = 0; i < n; i++) head[i + 1] = head[i] + deg[i];
  const to = new Int32Array(head[n]);
  const w = new Float64Array(head[n]);
  const cursor = new Int32Array(n);
  for (let i = 0; i < n; i++) cursor[i] = head[i];
  for (const [a, b] of segments) {
    const weight = haversineM(points[a], points[b]);
    const ia = cursor[a];
    cursor[a] = ia + 1;
    to[ia] = b;
    w[ia] = weight;
    const ib = cursor[b];
    cursor[b] = ib + 1;
    to[ib] = a;
    w[ib] = weight;
  }
  return { n, head, to, w };
}

let heapV = new Int32Array(16);
let heapD = new Float64Array(16);
const NO_TARGETS = new Uint8Array(0);

function pathsFrom(
  adj: Adj,
  cache: Map<number, PathRow>,
  src: number,
  targets: Uint8Array,
  targetCount: number,
  limit: number,
): PathRow {
  const hit = cache.get(src);
  if (hit) return hit;
  const found = dijkstra(adj, src, targets, targetCount, limit);
  cache.set(src, found);
  return found;
}

function shortestPath(adj: Adj, cache: Map<number, PathRow>, from: number, to: number): number[] {
  if (from === to) return [from];
  const { prev } = pathsFrom(adj, cache, from, NO_TARGETS, 0, Number.POSITIVE_INFINITY);
  const path: number[] = [];
  let cur = to;
  const guard = prev.length + 1;
  for (let steps = 0; cur !== from; steps++) {
    path.push(cur);
    cur = prev[cur];
    if (cur < 0 || steps > guard) return [from, to];
  }
  path.push(from);
  path.reverse();
  return path;
}

function dijkstra(adj: Adj, src: number, targets: Uint8Array, targetCount: number, limit: number): PathRow {
  const n = adj.n;
  const dist = new Float64Array(n);
  dist.fill(Number.POSITIVE_INFINITY);
  const prev = new Int32Array(n);
  prev.fill(-1);
  dist[src] = 0;
  const cap = Math.max(16, adj.w.length + 1);
  if (heapV.length < cap) {
    heapV = new Int32Array(cap);
    heapD = new Float64Array(cap);
  }
  let size = 0;
  const up = (index: number) => {
    let i = index;
    const d = heapD[i];
    const v = heapV[i];
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (d >= heapD[parent]) break;
      heapV[i] = heapV[parent];
      heapD[i] = heapD[parent];
      i = parent;
    }
    heapV[i] = v;
    heapD[i] = d;
  };
  const down = (index: number) => {
    const d = heapD[index];
    const v = heapV[index];
    let i = index;
    for (;;) {
      let smallest = i;
      let sd = d;
      const left = i * 2 + 1;
      const right = left + 1;
      if (left < size && heapD[left] < sd) {
        smallest = left;
        sd = heapD[left];
      }
      if (right < size && heapD[right] < sd) {
        smallest = right;
        sd = heapD[right];
      }
      if (smallest === i) break;
      heapV[i] = heapV[smallest];
      heapD[i] = heapD[smallest];
      i = smallest;
    }
    heapV[i] = v;
    heapD[i] = d;
  };
  heapV[size] = src;
  heapD[size] = 0;
  size += 1;
  let leftTargets = targets.length === n ? targetCount : 0;
  const { head, to, w } = adj;
  while (size > 0) {
    const v = heapV[0];
    const d = heapD[0];
    size -= 1;
    if (size > 0) {
      heapV[0] = heapV[size];
      heapD[0] = heapD[size];
      down(0);
    }
    if (d !== dist[v]) continue;
    if (d > limit) break;
    if (leftTargets > 0 && targets[v] === 1) {
      leftTargets -= 1;
      if (leftTargets === 0) break;
    }
    for (let e = head[v]; e < head[v + 1]; e++) {
      const next = d + w[e];
      const u = to[e];
      if (next < dist[u]) {
        dist[u] = next;
        prev[u] = v;
        heapV[size] = u;
        heapD[size] = next;
        size += 1;
        up(size - 1);
      }
    }
  }
  return { dist, prev };
}

/**
 * Shortest duplicate trails so a walk from `start` covers every segment.
 * An even vertex other than the start is never a strictly better open end,
 * so only the start and the odd-degree vertices are tried. Closed is first;
 * a later end replaces it only when the added length drops by more than 1e-6.
 */
function bestCover(
  adj: Adj,
  cache: Map<number, PathRow>,
  odd: number[],
  start: number,
): { pairs: Array<[number, number]> } {
  const n = adj.n;
  const inUniverse = new Uint8Array(n);
  const universe: number[] = [];
  for (const vertex of odd) {
    if (inUniverse[vertex] === 1) continue;
    inUniverse[vertex] = 1;
    universe.push(vertex);
  }
  if (inUniverse[start] !== 1) {
    inUniverse[start] = 1;
    universe.push(start);
  }
  const k = universe.length;
  if (k <= 1) return { pairs: [] };

  const indexOf = new Int32Array(n);
  indexOf.fill(-1);
  for (let i = 0; i < k; i++) indexOf[universe[i]] = i;
  const dist = pairwise(adj, cache, universe, inUniverse, Number.POSITIVE_INFINITY);
  if (k <= MATCH_EXACT_LIMIT) return exactCover(dist, universe, indexOf, odd, start);
  return { pairs: greedyCover(dist, universe, indexOf, odd, start).pairs };
}

function pairwise(
  adj: Adj,
  cache: Map<number, PathRow>,
  universe: number[],
  targets: Uint8Array,
  limit: number,
): Float64Array {
  const k = universe.length;
  const dist = new Float64Array(k * k);
  for (let i = 0; i < k; i++) {
    const row = pathsFrom(adj, cache, universe[i], targets, k, limit).dist;
    const base = i * k;
    for (let j = 0; j < k; j++) dist[base + j] = i === j ? 0 : row[universe[j]];
  }
  return dist;
}

function endpointMask(odd: number[], start: number, end: number, indexOf: Int32Array): number {
  let mask = 0;
  for (const vertex of odd) mask |= 1 << indexOf[vertex];
  if (start !== end) {
    mask ^= 1 << indexOf[start];
    mask ^= 1 << indexOf[end];
  }
  return mask;
}

function exactCover(
  dist: Float64Array,
  universe: number[],
  indexOf: Int32Array,
  odd: number[],
  start: number,
): { pairs: Array<[number, number]> } {
  const k = universe.length;
  const full = (1 << k) - 1;
  const dp = new Float64Array(full + 1);
  dp.fill(Number.POSITIVE_INFINITY);
  const choice = new Int16Array(full + 1);
  choice.fill(-1);
  dp[0] = 0;
  for (let mask = 1; mask <= full; mask++) {
    let bits = 0;
    for (let x = mask; x !== 0; x &= x - 1) bits += 1;
    if ((bits & 1) !== 0) continue;
    const lowest = mask & -mask;
    const i = 31 - Math.clz32(lowest);
    let rest = mask ^ lowest;
    let best = Number.POSITIVE_INFINITY;
    let pick = -1;
    while (rest !== 0) {
      const jBit = rest & -rest;
      const j = 31 - Math.clz32(jBit);
      const prev = mask ^ lowest ^ jBit;
      const cost = dp[prev] + dist[i * k + j];
      if (cost < best) {
        best = cost;
        pick = i * k + j;
      }
      rest ^= jBit;
    }
    dp[mask] = best;
    choice[mask] = pick;
  }

  let bestCost = Number.POSITIVE_INFINITY;
  let bestMask = 0;
  const consider = (end: number) => {
    const mask = endpointMask(odd, start, end, indexOf);
    const cost = dp[mask];
    if (cost < bestCost - 1e-6) {
      bestCost = cost;
      bestMask = mask;
    }
  };
  consider(start);
  for (const end of odd) {
    if (end !== start) consider(end);
  }
  return { pairs: pairsFromChoice(bestMask, choice, universe, k) };
}

function pairsFromChoice(
  mask: number,
  choice: Int16Array,
  universe: number[],
  k: number,
): Array<[number, number]> {
  const pairs: Array<[number, number]> = [];
  let guard = k;
  while (mask !== 0 && guard > 0) {
    guard -= 1;
    const picked = choice[mask];
    if (picked < 0) break;
    const i = Math.floor(picked / k);
    const j = picked % k;
    pairs.push([universe[i], universe[j]]);
    mask ^= (1 << i) | (1 << j);
  }
  return pairs;
}

function greedyCover(
  dist: Float64Array,
  universe: number[],
  indexOf: Int32Array,
  odd: number[],
  start: number,
): { pairs: Array<[number, number]>; covered: boolean } {
  const k = universe.length;
  const members = new Uint8Array(k);
  for (const vertex of odd) members[indexOf[vertex]] = 1;
  if (k > GREEDY_ALL_ENDS) return nearestCover(dist, universe, members, indexOf[start]);

  const pairCount = (k * (k - 1)) / 2;
  const pi = new Int32Array(pairCount);
  const pj = new Int32Array(pairCount);
  const pd = new Float64Array(pairCount);
  let p = 0;
  for (let i = 0; i < k; i++) {
    const base = i * k;
    for (let j = i + 1; j < k; j++) {
      pi[p] = i;
      pj[p] = j;
      pd[p] = dist[base + j];
      p += 1;
    }
  }
  const order = new Int32Array(pairCount);
  for (let i = 0; i < pairCount; i++) order[i] = i;
  order.sort((a, b) => {
    const da = pd[a];
    const db = pd[b];
    const fa = Number.isFinite(da);
    const fb = Number.isFinite(db);
    if (fa !== fb) return fa ? -1 : 1;
    if (da < db) return -1;
    if (da > db) return 1;
    return a - b;
  });

  let bestCost = Number.POSITIVE_INFINITY;
  let bestEdges = new Int32Array(0);
  const used = new Uint8Array(k);
  const chosen = new Int32Array(k >> 1);
  const consider = () => {
    used.fill(0);
    let chosenN = 0;
    let cost = 0;
    let need = 0;
    for (let i = 0; i < k; i++) need += members[i];
    for (let e = 0; e < pairCount && need > 0; e++) {
      const id = order[e];
      const a = pi[id];
      const b = pj[id];
      if (!Number.isFinite(pd[id])) break;
      if (members[a] === 0 || members[b] === 0 || used[a] !== 0 || used[b] !== 0) continue;
      used[a] = 1;
      used[b] = 1;
      cost += pd[id];
      chosen[chosenN] = id;
      chosenN += 1;
      need -= 2;
    }
    if (need !== 0 || !(cost < bestCost - 1e-6)) return;
    bestCost = cost;
    bestEdges = chosen.slice(0, chosenN);
  };

  consider();
  const startIndex = indexOf[start];
  for (const end of odd) {
    if (end === start) continue;
    const endIndex = indexOf[end];
    members[startIndex] ^= 1;
    members[endIndex] ^= 1;
    consider();
    members[startIndex] ^= 1;
    members[endIndex] ^= 1;
  }

  const pairs: Array<[number, number]> = [];
  for (let i = 0; i < bestEdges.length; i++) {
    const id = bestEdges[i];
    pairs.push([universe[pi[id]], universe[pj[id]]]);
  }
  return { pairs, covered: Number.isFinite(bestCost) };
}

/** One closed covering, plus an open walk when the start is odd. For very large odd sets. */
function nearestCover(
  dist: Float64Array,
  universe: number[],
  oddMembers: Uint8Array,
  startIndex: number,
): { pairs: Array<[number, number]>; covered: boolean } {
  const closed = nearestMatching(dist, universe, oddMembers);
  if (oddMembers[startIndex] !== 1) return { pairs: closed.pairs, covered: closed.covered };
  let mate = -1;
  let mateDist = Number.NEGATIVE_INFINITY;
  const row = startIndex * universe.length;
  for (let j = 0; j < universe.length; j++) {
    if (j === startIndex || oddMembers[j] !== 1) continue;
    if (dist[row + j] > mateDist) {
      mateDist = dist[row + j];
      mate = j;
    }
  }
  if (mate < 0) return { pairs: closed.pairs, covered: closed.covered };
  const openMembers = oddMembers.slice();
  openMembers[startIndex] = 0;
  openMembers[mate] = 0;
  const open = nearestMatching(dist, universe, openMembers);
  if (open.covered && open.cost < closed.cost - 1e-6) return { pairs: open.pairs, covered: true };
  return { pairs: closed.pairs, covered: closed.covered };
}

function nearestMatching(
  dist: Float64Array,
  universe: number[],
  members: Uint8Array,
): { cost: number; pairs: Array<[number, number]>; covered: boolean } {
  const k = universe.length;
  const used = new Uint8Array(k);
  const pairs: Array<[number, number]> = [];
  let cost = 0;
  let left = 0;
  for (let i = 0; i < k; i++) left += members[i];
  for (let i = 0; i < k; i++) {
    if (members[i] !== 1 || used[i] === 1) continue;
    let bestJ = -1;
    let best = Number.POSITIVE_INFINITY;
    const row = i * k;
    for (let j = i + 1; j < k; j++) {
      if (members[j] !== 1 || used[j] === 1) continue;
      const d = dist[row + j];
      if (d < best) {
        best = d;
        bestJ = j;
      }
    }
    if (bestJ < 0 || !Number.isFinite(best)) break;
    used[i] = 1;
    used[bestJ] = 1;
    cost += best;
    pairs.push([universe[i], universe[bestJ]]);
    left -= 2;
  }
  return { cost, pairs, covered: left === 0 };
}

interface HalfEdge {
  to: number;
  used: { value: boolean };
}

function eulerWalk(start: number, n: number, mult: Map<string, number>): number[] {
  const adj: HalfEdge[][] = Array.from({ length: n }, () => []);
  for (const [key, count] of mult) {
    const [as, bs] = key.split(":");
    const a = Number(as);
    const b = Number(bs);
    for (let copy = 0; copy < count; copy++) {
      const used = { value: false };
      adj[a].push({ to: b, used });
      adj[b].push({ to: a, used });
    }
  }
  const stack = [start];
  const walk: number[] = [];
  while (stack.length > 0) {
    const u = stack[stack.length - 1];
    const list = adj[u];
    while (list.length > 0 && list[list.length - 1].used.value) list.pop();
    const next = list.length > 0 ? list[list.length - 1] : undefined;
    if (next && !next.used.value) {
      next.used.value = true;
      list.pop();
      stack.push(next.to);
    } else {
      const popped = stack.pop();
      if (popped != null) walk.push(popped);
    }
  }
  walk.reverse();
  return walk;
}
